/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { JobStates } from '../../../../../common/src/domain/job/JobStates';
import { JobTypes } from '../../../../../common/src/domain/job/JobTypes';
import { compressedFilesToDecompositionInspectionStandardTypes } from '../../../../../common/src/domain/worker/compressedFilesToDecompositionInspectionStandardTypes';
import { InspectionStandardTypes } from '../../../../../common/src/enums/InspectionStandardTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { EditPanelRow, EditPanelSection, PanelBody } from '../../elements';
import { Spinner } from '../../spinners';
import { DisplayMessage } from '../../utils/displayMessage';
import { useReactAsync } from '../../utils/useReactAsync';
import { AssetDropzone } from '../Common/AssetDropzone';

async function loadStatus({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/jobs/{jobType}/status', { surveyId, jobType: JobTypes.CompressedFiles });
    return result;
}

export function CompressedFiles(props: {
    objectId: string;
    surveyId: string;
    onConverted: () => unknown;
    inspectionStandardType: InspectionStandardTypes;
}) {
    const translate = useTranslate();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [uploadedAsset, setUploadedAsset] = React.useState<
        | {
              assetId: string;
              filename: string;
              filesize: number;
          }
        | undefined
    >();

    const { data, reload } = useReactAsync(loadStatus, { surveyId: props.surveyId }, []);

    async function finishUpload() {
        if (uploadedAsset) {
            await openApiFetchPost(
                '/api/surveys/{surveyId}/jobs/{jobType}/finishUpload',
                { surveyId: props.surveyId, jobType: JobTypes.CompressedFiles },
                {
                    assetId: uploadedAsset.assetId,
                    filename: uploadedAsset.filename,
                    filesize: uploadedAsset.filesize,
                    objectId: props.objectId,
                    options: {},
                },
            );
            await reload();
            setUploadedAsset(undefined);
        }
    }

    React.useEffect(() => {
        if (uploadedAsset) {
            void finishUpload();
        }
    }, [uploadedAsset]);

    React.useEffect(() => {
        let loadStatusTimer: number;
        if (data?.status === JobStates.Processing) {
            loadStatusTimer = window.setInterval(async () => reload(), 5000);
        }
        return () => {
            if (loadStatusTimer) {
                props.onConverted();
                window.clearInterval(loadStatusTimer);
            }
        };
    }, [data]);

    return (
        <PanelBody>
            <EditPanelRow>
                {data?.status === JobStates.Processing && (
                    <>
                        <Spinner />
                        <p className="text-center fw200 fs16">{data.message}</p>
                    </>
                )}
                {data?.status === JobStates.Failed && (
                    <DisplayMessage message={`${translate('worker.lastJobFailed')}: ${data?.message ?? ''}`} messageType="error" />
                )}

                {data?.status && [JobStates.NoJob, JobStates.Finished, JobStates.Failed].includes(data?.status) && uploadedAsset === undefined && (
                    <>
                        {errorMessage && (
                            <DisplayMessage
                                message={errorMessage === 'Dropzone: FILE_TOO_BIG' ? translate('asset.errorFileTooBig') : errorMessage}
                                messageType="error"
                            />
                        )}
                        <EditPanelSection>
                            <AssetDropzone
                                acceptedFiles=".zip"
                                onComplete={(file) => {
                                    if (file.status === 'success') {
                                        setUploadedAsset({ assetId: file.s3Id, filename: file.name, filesize: file.upload!.total });
                                    }
                                }}
                                maxFileSizePerType={[{ extension: 'zip', sizeMb: 35_000 }]}
                                onError={(e) => setErrorMessage(e)}
                                objectId={props.objectId}
                                surveyId={props.surveyId}
                                autoProcessQueue={true}
                                maxFiles={1}
                            />
                        </EditPanelSection>
                        {compressedFilesToDecompositionInspectionStandardTypes.includes(props.inspectionStandardType) ? (
                            <div className="fs14 fw200">{translate('worker.compressedImages')}</div>
                        ) : (
                            <div className="fs14 fw200">{translate('worker.uploadZip')}</div>
                        )}
                    </>
                )}
            </EditPanelRow>
        </PanelBody>
    );
}
